import { useState, useEffect } from 'react'
import { Dialog, DialogPortal } from '@/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { 
  format, 
  isSameDay, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isToday, 
  isBefore, 
  startOfToday,
  setHours,
  setMinutes
} from 'date-fns'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { CREATE_BOOKING_ENDPOINT_PRODUCTION } from '@/config/endpoints'
import { addBookingConfirmationMessageToChat } from '@/lib/chatData'

interface SimpleAvailabilityDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSendOffer?: (
    priceRangeMin: number,
    priceRangeMax: number,
    availableDates: Date[],
    estimatedTime?: number | 'unknown',
    availableTimeSlots?: Record<string, string[]>
  ) => void
  onRecordBooking?: (bookingDate: Date, bookingTime?: string) => void
  chatId?: string
  artistId?: string
  onBookingCreated?: () => void
}

// Generate time options: 1 to 24 hours in 0.5 increments
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = (i + 2) * 0.5
  return {
    value: hours,
    label: hours % 1 === 0 ? `${hours.toFixed(0)}h` : `${hours}h`
  }
})

// Generate time slots: 9 AM to 9 PM in 30-minute increments (4x6 grid = 24 slots)
const generateTimeSlots = (): string[] => {
  const slots: string[] = []
  for (let hour = 9; hour < 21; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      slots.push(time)
    }
  }
  return slots
}

const TIME_SLOTS = generateTimeSlots()

export function SimpleAvailabilityDialog({
  isOpen,
  onOpenChange,
  onSendOffer,
  onRecordBooking,
  chatId,
  artistId,
  onBookingCreated
}: SimpleAvailabilityDialogProps) {
  const { user } = useAuthState()
  const { userData } = useUserData()
  const [mode, setMode] = useState<'offer' | 'record'>('offer')
  const [priceRangeMin, setPriceRangeMin] = useState<string>('')
  const [priceRangeMax, setPriceRangeMax] = useState<string>('')
  const [selectedDates, setSelectedDates] = useState<Date[]>([])
  const [estimatedTime, setEstimatedTime] = useState<number | 'unknown'>(1)
  const [showCalendar, setShowCalendar] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [bookingDate, setBookingDate] = useState<Date>(new Date())
  const [bookingTime, setBookingTime] = useState<string>('09:00')
  const [showingTimeSlots, setShowingTimeSlots] = useState(false)
  const [currentTimePickerDate, setCurrentTimePickerDate] = useState<Date | null>(null)
  const [availableSlots, setAvailableSlots] = useState<{ [key: string]: Set<string> }>({})
  const [isSelectingTimeRange, setIsSelectingTimeRange] = useState(false)
  const [timeRangeStart, setTimeRangeStart] = useState<string | null>(null)
  const [hasMovedDuringSelection, setHasMovedDuringSelection] = useState(false)
  const [selectionMode, setSelectionMode] = useState<'add' | 'remove' | null>(null)
  const [applyFeedback, setApplyFeedback] = useState(false)
  const [slotsBeforeDrag, setSlotsBeforeDrag] = useState<{ [key: string]: Set<string> }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recordError, setRecordError] = useState<string | null>(null)
  const [isTime4TatsActive, setIsTime4TatsActive] = useState(false)

  // Reset when dialog opens
  useEffect(() => {
    if (!isOpen) {
      setMode('offer')
      setPriceRangeMin('')
      setPriceRangeMax('')
      setSelectedDates([])
      setEstimatedTime(1)
      setShowCalendar(false)
      setErrors({})
      setBookingDate(new Date())
      setBookingTime('09:00')
      setShowingTimeSlots(false)
      setCurrentTimePickerDate(null)
      setAvailableSlots({})
      setIsSelectingTimeRange(false)
      setTimeRangeStart(null)
      setHasMovedDuringSelection(false)
      setSelectionMode(null)
      setApplyFeedback(false)
      setSlotsBeforeDrag({})
      setIsSubmitting(false)
      setRecordError(null)
      setIsTime4TatsActive(false)
    }
  }, [isOpen])

  const handleDateSelect = (date: Date) => {
    setSelectedDates(prev => {
      const isAlreadySelected = prev.some(d => isSameDay(d, date))
      if (isAlreadySelected) {
        // Remove the date if already selected
        return prev.filter(d => !isSameDay(d, date))
      } else {
        // Add the date if not selected
        return [...prev, date].sort((a, b) => a.getTime() - b.getTime())
      }
    })
  }




  const handleSendOffer = () => {
    if (selectedDates.length === 0) {
      setErrors({ dates: 'Please select at least one available date' })
      return
    }

    if (Object.keys(availableSlots).length === 0) {
      setErrors({ dates: 'Please select at least one time slot' })
      return
    }

    const minPrice = priceRangeMin ? parseFloat(priceRangeMin) : 0
    const maxPrice = priceRangeMax ? parseFloat(priceRangeMax) : 0

    // Convert availableSlots Set to serializable format
    const availableTimeSlotsData: Record<string, string[]> = {}
    Object.entries(availableSlots).forEach(([dateKey, slots]) => {
      availableTimeSlotsData[dateKey] = Array.from(slots)
    })

    onSendOffer?.(minPrice, maxPrice, selectedDates, estimatedTime, availableTimeSlotsData)
    onOpenChange(false)
  }

  const handleConfirmBooking = async () => {
    console.log('[SimpleAvailabilityDialog:confirmBooking] Button clicked')
    console.log('[SimpleAvailabilityDialog:confirmBooking] Current state:', {
      bookingDate,
      bookingTime,
      user: !!user,
      chatId,
      artistId
    })

    if (!bookingDate || !bookingTime || !user) {
      console.log('[SimpleAvailabilityDialog:confirmBooking] Missing basic information')
      setRecordError('Missing required booking information')
      return
    }

    // If chatId and artistId are not provided, just call the callback without API
    if (!chatId || !artistId) {
      console.log('[SimpleAvailabilityDialog:confirmBooking] No chatId/artistId, calling callback without API')
      onRecordBooking?.(bookingDate, bookingTime)
      onOpenChange(false)
      return
    }

    setIsSubmitting(true)
    setRecordError(null)

    try {
      // Parse the booking time (format: "HH:MM") and combine with booking date
      const [hours, minutes] = bookingTime.split(':').map(Number)
      const bookingDateTime = setMinutes(setHours(bookingDate, hours), minutes)
      const bookingTimestamp = bookingDateTime.getTime() // Unix timestamp in milliseconds

      console.log('[SimpleAvailabilityDialog:confirmBooking] 🎫 Starting booking confirmation flow')
      console.log('[SimpleAvailabilityDialog:confirmBooking] Selection details:', {
        bookingDate: bookingDate.toISOString(),
        bookingTime: bookingTime,
        bookingDateTime: bookingDateTime.toISOString(),
        bookingTimestamp,
        user: user.email,
        artistId: artistId,
        chatId: chatId
      })

      // STEP 1: Determine pricing
      let finalMinPrice: number
      let finalMaxPrice: number
      
      finalMinPrice = priceRangeMin ? parseFloat(priceRangeMin) : 0
      finalMaxPrice = priceRangeMax ? parseFloat(priceRangeMax) : 0

      // STEP 2: Create the confirmation message FIRST to get its messageId
      let confirmationMessageId: string | undefined
      if (chatId && user) {
        try {
          const finalEstimatedTimeForMessage = estimatedTime === 'unknown' ? 'unknown' : (estimatedTime || 1)
          confirmationMessageId = await addBookingConfirmationMessageToChat(
            chatId,
            bookingDate,
            finalMinPrice,
            finalMaxPrice,
            finalEstimatedTimeForMessage,
            user.uid,
            'artist',
            bookingTime
          )
          console.log('[SimpleAvailabilityDialog:confirmBooking] ✅ Booking confirmation message added to chat with ID:', confirmationMessageId)
        } catch (msgErr) {
          console.error('[SimpleAvailabilityDialog:confirmBooking] ❌ Failed to create confirmation message:', msgErr)
          throw new Error('Failed to create booking confirmation message')
        }
      }

      // STEP 3: Create the booking with the messageId
      const finalEstimatedTime = estimatedTime === 'unknown' ? 1 : (estimatedTime || 1)
      
      const clientData: Record<string, string | boolean> = {}
      
      // Add auto-populated fields
      if (user?.email) clientData.email = user.email
      if (userData?.instagram) clientData.username = userData.instagram
      
      const requestData = {
        chatId: chatId,
        clientData: clientData,
        timestamp: bookingTimestamp,
        bookingDateTime: bookingDateTime.toISOString(),
        artistId: artistId,
        estimatedTime: finalEstimatedTime,
        priceRangeMin: finalMinPrice,
        priceRangeMax: finalMaxPrice,
        messageId: confirmationMessageId // Link booking to the confirmation message
      }

      console.log('[SimpleAvailabilityDialog:confirmBooking] Booking request payload:', requestData)

      // Call the create_booking endpoint
      console.log('[SimpleAvailabilityDialog:confirmBooking] Sending booking creation request to backend')
      const response = await fetch(CREATE_BOOKING_ENDPOINT_PRODUCTION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      console.log('[SimpleAvailabilityDialog:confirmBooking] Backend response:', {
        status: response.status,
        statusText: response.statusText
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('[SimpleAvailabilityDialog:confirmBooking] ❌ Backend error:', errorData)
        throw new Error(errorData.message || `Failed to create booking: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('[SimpleAvailabilityDialog:confirmBooking] ✅ Booking created in backend')
      console.log('[SimpleAvailabilityDialog:confirmBooking] Backend response:', result)

      // Notify callbacks
      if (onBookingCreated) {
        console.log('[SimpleAvailabilityDialog:confirmBooking] Calling onBookingCreated callback')
        onBookingCreated()
      }
      
      onRecordBooking?.(bookingDate, bookingTime)
      
      console.log('[SimpleAvailabilityDialog:confirmBooking] ✅ Booking confirmation flow complete!')
      onOpenChange(false)
    } catch (err) {
      console.error('[SimpleAvailabilityDialog:confirmBooking] ❌ Error creating booking:', err)
      console.error('[SimpleAvailabilityDialog:confirmBooking] Error details:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err)
      })
      setRecordError(err instanceof Error ? err.message : 'Failed to create booking. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleTimeSlot = (date: Date | null, timeSlot: string) => {
    if (!date) return
    
    try {
      const dateKey = format(date, 'yyyy-MM-dd')
      const newSlots = { ...availableSlots }
      
      if (!newSlots[dateKey]) {
        newSlots[dateKey] = new Set()
      }
      
      if (newSlots[dateKey].has(timeSlot)) {
        newSlots[dateKey].delete(timeSlot)
        if (newSlots[dateKey].size === 0) {
          delete newSlots[dateKey]
        }
      } else {
        newSlots[dateKey].add(timeSlot)
      }
      
      setAvailableSlots(newSlots)
    } catch (e) {
      console.error('Error toggling time slot:', e)
    }
  }

  const isTimeSlotSelected = (date: Date | null, timeSlot: string): boolean => {
    if (!date) return false
    try {
      const dateKey = format(date, 'yyyy-MM-dd')
      return availableSlots[dateKey]?.has(timeSlot) ?? false
    } catch (e) {
      return false
    }
  }

  const applyTimeSlotToAllDates = (timeSlot: string) => {
    const newSlots = { ...availableSlots }
    selectedDates.forEach(date => {
      const dateKey = format(date, 'yyyy-MM-dd')
      if (!newSlots[dateKey]) {
        newSlots[dateKey] = new Set()
      }
      newSlots[dateKey].add(timeSlot)
    })
    setAvailableSlots(newSlots)
  }

  const openTimePicker = () => {
    if (selectedDates.length > 0) {
      setCurrentTimePickerDate(selectedDates[0])
      setShowingTimeSlots(true)
    }
  }

  const handleTimeSlotMouseDown = (timeSlot: string, date: Date | null) => {
    setIsSelectingTimeRange(true)
    setTimeRangeStart(timeSlot)
    setHasMovedDuringSelection(false)
    
    // Save the current state before we start modifying
    const currentState: { [key: string]: Set<string> } = {}
    Object.keys(availableSlots).forEach(key => {
      currentState[key] = new Set(availableSlots[key])
    })
    setSlotsBeforeDrag(currentState)
    
    // Determine if we're in add or remove mode based on current state
    const isCurrentlySelected = isTimeSlotSelected(date, timeSlot)
    if (isCurrentlySelected) {
      setSelectionMode('remove')
    } else {
      setSelectionMode('add')
    }
  }

  const handleTimeSlotMouseEnter = (timeSlot: string, date: Date | null) => {
    if (!isSelectingTimeRange || !timeRangeStart || !date) return

    // If this is a different slot than the start, mark that we've moved
    if (timeSlot !== timeRangeStart) {
      setHasMovedDuringSelection(true)
    }

    const startIndex = TIME_SLOTS.indexOf(timeRangeStart)
    const endIndex = TIME_SLOTS.indexOf(timeSlot)

    if (startIndex === -1 || endIndex === -1) return

    const [minIndex, maxIndex] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex]

    const dateKey = format(date, 'yyyy-MM-dd')
    
    // Start fresh from the state before dragging
    const newSlots: { [key: string]: Set<string> } = {}
    Object.keys(slotsBeforeDrag).forEach(key => {
      newSlots[key] = new Set(slotsBeforeDrag[key])
    })
    
    if (!newSlots[dateKey]) {
      newSlots[dateKey] = new Set()
    }

    if (selectionMode === 'add') {
      // Add mode: add all slots in the range
      for (let i = minIndex; i <= maxIndex; i++) {
        const slot = TIME_SLOTS[i]
        newSlots[dateKey].add(slot)
      }
    } else if (selectionMode === 'remove') {
      // Remove mode: remove all slots in the range
      for (let i = minIndex; i <= maxIndex; i++) {
        const slot = TIME_SLOTS[i]
        newSlots[dateKey].delete(slot)
      }
    }

    // Clean up empty sets
    if (newSlots[dateKey] && newSlots[dateKey].size === 0) {
      delete newSlots[dateKey]
    }

    setAvailableSlots(newSlots)
  }

  const handleTimeSlotMouseUp = (timeSlot: string, date: Date | null) => {
    // If we haven't moved, this is just a click - toggle the single slot
    if (!hasMovedDuringSelection && timeRangeStart === timeSlot && date) {
      toggleTimeSlot(date, timeSlot)
    }
    
    setIsSelectingTimeRange(false)
    setTimeRangeStart(null)
    setHasMovedDuringSelection(false)
    setSelectionMode(null)
    setSlotsBeforeDrag({})
  }

  const handleTime4TatsClick = () => {
    // May 9th - create a date for May 9 of the current year (if it hasn't passed) or next year
    const now = new Date()
    let time4tatsDate = new Date(now.getFullYear(), 4, 9) // Month is 0-indexed, so 4 = May
    
    // If the date has already passed this year, use next year's May 9th
    if (time4tatsDate < now) {
      time4tatsDate = new Date(now.getFullYear() + 1, 4, 9)
    }
    
    setBookingDate(time4tatsDate)
    setIsTime4TatsActive(!isTime4TatsActive)
  }

  // When date changes, deactivate time4tats if it's not May 9th
  useEffect(() => {
    if (isTime4TatsActive) {
      const isMay9 = bookingDate.getMonth() === 4 && bookingDate.getDate() === 9
      if (!isMay9) {
        setIsTime4TatsActive(false)
      }
    }
  }, [bookingDate, isTime4TatsActive])

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
            "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {mode === 'offer' ? 'Available Times' : 'Record Booking'}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="p-2 -mr-2"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-100px)]">
            <div className="space-y-2">
              {mode === 'offer' ? (
                <>
                  {/* Price Range */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Price Range (€)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Leave empty if unknown
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="150"
                        value={priceRangeMin}
                        onChange={(e) => setPriceRangeMin(e.target.value)}
                        className={cn(errors.priceMin && "border-destructive")}
                      />
                      <div className="text-lg font-medium text-muted-foreground">
                        –
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="250"
                        value={priceRangeMax}
                        onChange={(e) => setPriceRangeMax(e.target.value)}
                        className={cn(errors.priceMax && "border-destructive")}
                      />
                    </div>
                    {errors.priceMin && (
                      <p className="text-xs text-destructive">{errors.priceMin}</p>
                    )}
                    {errors.priceMax && (
                      <p className="text-xs text-destructive">{errors.priceMax}</p>
                    )}
                  </div>

                  {/* Estimated Time Dropdown */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Estimated time for this session?
                    </Label>
                    <p className="text-xs text-muted-foreground">
                    </p>
                    <select
                      value={estimatedTime === 'unknown' ? 'unknown' : estimatedTime}
                      onChange={(e) => {
                        const val = e.target.value
                        setEstimatedTime(val === 'unknown' ? 'unknown' : parseFloat(val))
                      }}
                      className="w-full px-3 py-2 text-base text-foreground bg-background border border-input rounded-md focus:outline-none focus:ring-0"
                    >
                      <option value="unknown">Unknown</option>
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Available Dates */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Select available dates
                    </Label>
                    <p className="text-xs text-muted-foreground">
                    </p>
                    <div className={cn("border border-input rounded-md", errors.dates && "border-destructive")}>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal border-0 rounded-[inherit]"
                        onClick={() => setShowCalendar(true)}
                        type="button"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {selectedDates.length === 0
                          ? 'Select dates'
                          : `${selectedDates.length} date${selectedDates.length > 1 ? 's' : ''} selected`}
                      </Button>
                    </div>
                    {errors.dates && (
                      <p className="text-xs text-destructive mt-1">{errors.dates}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <Button
                      onClick={handleSendOffer}
                      className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
                      size="lg"
                    >
                      Send to Client
                    </Button>
                    <div className="w-full border border-input rounded-[var(--card-radius)]">
                      <Button
                        variant="outline"
                        onClick={() => setMode('record')}
                        className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] border-0"
                        size="lg"
                      >
                        Time Already Agreed?
                      </Button>
                    </div>
                    {errors.prices && (
                      <p className="text-xs text-destructive text-center">{errors.prices}</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Price Range */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Price Range (€)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Leave empty if unknown
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="150"
                        value={priceRangeMin}
                        onChange={(e) => setPriceRangeMin(e.target.value)}
                        className={cn(errors.priceMin && "border-destructive")}
                      />
                      <div className="text-lg font-medium text-muted-foreground">
                        –
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="250"
                        value={priceRangeMax}
                        onChange={(e) => setPriceRangeMax(e.target.value)}
                        className={cn(errors.priceMax && "border-destructive")}
                      />
                    </div>
                    {errors.priceMin && (
                      <p className="text-xs text-destructive">{errors.priceMin}</p>
                    )}
                    {errors.priceMax && (
                      <p className="text-xs text-destructive">{errors.priceMax}</p>
                    )}
                  </div>

                  {/* Estimated Time Dropdown */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Estimated time for this session?
                    </Label>
                    <p className="text-xs text-muted-foreground">
                    </p>
                    <select
                      value={estimatedTime === 'unknown' ? 'unknown' : estimatedTime}
                      onChange={(e) => {
                        const val = e.target.value
                        setEstimatedTime(val === 'unknown' ? 'unknown' : parseFloat(val))
                      }}
                      className="w-full px-3 py-2 text-base text-foreground bg-background border border-input rounded-md focus:outline-none focus:ring-0"
                    >
                      <option value="unknown">Unknown</option>
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Booking Date Picker */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Select booking date
                    </Label>
                    <p className="text-xs text-muted-foreground">
                    </p>
                    <div className="flex gap-2">
                      <div className="flex-[7] border border-input rounded-md">
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal border-0 rounded-[inherit]",
                            isTime4TatsActive && "time4tats-btn"
                          )}
                          onClick={() => setShowCalendar(true)}
                          type="button"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {format(bookingDate, 'PPP')}
                        </Button>
                      </div>
                      <Button
                        onClick={handleTime4TatsClick}
                        className={`flex-[3] whitespace-nowrap min-w-fit flex items-center justify-center gap-2 text-xs rounded-md font-medium transition-all ${
                          isTime4TatsActive ? "time4tats-btn" : "time4tats-btn-inactive"
                        }`}
                        variant="ghost"
                        type="button"
                      >
                        <span>time4tats</span>
                      </Button>
                    </div>
                  </div>

                  {/* Booking Time Picker */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Select booking time
                    </Label>
                    <p className="text-xs text-muted-foreground">
                    </p>
                    <div className="border border-input rounded-md">
                      <select
                        value={bookingTime}
                        onChange={(e) => setBookingTime(e.target.value)}
                        className="w-full px-4 py-2 text-sm bg-background text-foreground border-0 rounded-[inherit] focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2rem'
                        }}
                      >
                        {TIME_SLOTS.map(slot => (
                          <option key={slot} value={slot}>
                            {slot}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Error Message */}
                  {recordError && (
                    <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md">
                      <p className="text-xs text-destructive">{recordError}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <Button
                      onClick={handleConfirmBooking}
                      disabled={isSubmitting}
                      className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
                      size="lg"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Confirming...
                        </span>
                      ) : (
                        'Confirm'
                      )}
                    </Button>
                    <div className="w-full border border-input rounded-[var(--card-radius)]">
                      <Button
                        variant="outline"
                        onClick={() => setMode('offer')}
                        disabled={isSubmitting}
                        className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] border-0"
                        size="lg"
                      >
                        Back
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>

      {/* Calendar Popup Dialog */}
      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[70] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-[70] translate-x-[-50%] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] p-0 overflow-hidden",
              "w-[90vw] max-w-[400px]"
            )}
          >
            {/* Calendar Content */}
            <div className="p-4 overflow-y-auto max-h-[85vh]">
              {showingTimeSlots ? (
                <>
                  {/* Time Picker Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold">Select Available Times</h3>
                    
                    {/* Date Selector */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-foreground">Select Date</Label>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {selectedDates.map(date => (
                          <div
                            key={date.toISOString()}
                            className={cn(
                              "border rounded-md",
                              isSameDay(date, currentTimePickerDate || selectedDates[0])
                                ? "border-input bg-primary"
                                : "border-input"
                            )}
                          >
                            <Button
                              variant={isSameDay(date, currentTimePickerDate || selectedDates[0]) ? 'default' : 'outline'}
                              onClick={() => setCurrentTimePickerDate(date)}
                              className="whitespace-nowrap border-0"
                              size="sm"
                            >
                              {format(date, 'MMM d')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Time Slots Grid */}
                    {currentTimePickerDate || selectedDates[0] ? (
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-foreground">
                          Times for {currentTimePickerDate ? format(currentTimePickerDate, 'MMM d') : selectedDates[0] ? format(selectedDates[0], 'MMM d') : ''}
                        </Label>
                        <div 
                          className="border border-input rounded-lg bg-background overflow-hidden"
                          onMouseLeave={() => {
                            setIsSelectingTimeRange(false)
                            setTimeRangeStart(null)
                            setHasMovedDuringSelection(false)
                            setSelectionMode(null)
                            setSlotsBeforeDrag({})
                          }}
                        >
                          <div className="grid grid-cols-6 gap-0">
                            {TIME_SLOTS.map((timeSlot, index) => {
                              const dateToUse = currentTimePickerDate || selectedDates[0]
                              const isSelected = isTimeSlotSelected(dateToUse, timeSlot)
                              // Add right border for all except last column, bottom border for all except last row
                              const borderRight = (index + 1) % 6 !== 0
                              const borderBottom = index < TIME_SLOTS.length - 6
                              
                              return (
                                <button
                                  key={timeSlot}
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    handleTimeSlotMouseDown(timeSlot, dateToUse)
                                  }}
                                  onMouseEnter={() => handleTimeSlotMouseEnter(timeSlot, dateToUse)}
                                  onMouseUp={(e) => {
                                    e.preventDefault()
                                    handleTimeSlotMouseUp(timeSlot, dateToUse)
                                  }}
                                  onTouchStart={() => {
                                    handleTimeSlotMouseDown(timeSlot, dateToUse)
                                  }}
                                  onTouchMove={(e) => {
                                    const touch = e.touches[0]
                                    const element = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLButtonElement
                                    if (element?.getAttribute('data-time-slot')) {
                                      const slot = element.getAttribute('data-time-slot')
                                      if (slot) {
                                        handleTimeSlotMouseEnter(slot, dateToUse)
                                      }
                                    }
                                  }}
                                  onTouchEnd={(e) => {
                                    e.preventDefault()
                                    handleTimeSlotMouseUp(timeSlot, dateToUse)
                                  }}
                                  data-time-slot={timeSlot}
                                  className={cn(
                                    "flex-1 py-3 px-2 text-xs font-medium transition-colors text-center select-none border-0 relative",
                                    "hover:bg-muted/70",
                                    isSelected && "bg-primary text-primary-foreground",
                                    !isSelected && "text-foreground bg-background",
                                    borderRight && "border-r border-r-input/30",
                                    borderBottom && "border-b border-b-input/30"
                                  )}
                                  type="button"
                                >
                                  {timeSlot}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground text-center">
                          Drag across to select multiple times
                        </p>
                      </div>
                    ) : null}

                    {/* Apply to All Button */}
                    <div className={cn("border rounded-md", applyFeedback ? "border-green-600" : "border-input")}>
                      <Button
                        variant={applyFeedback ? "default" : "outline"}
                        onClick={() => {
                          const dateToUse = currentTimePickerDate || selectedDates[0]
                          if (dateToUse) {
                            try {
                              const selectedTimeSlots = Array.from(availableSlots[format(dateToUse, 'yyyy-MM-dd')] || [])
                              if (selectedTimeSlots.length > 0) {
                                selectedTimeSlots.forEach(slot => applyTimeSlotToAllDates(slot))
                                setApplyFeedback(true)
                                setTimeout(() => setApplyFeedback(false), 2000)
                              }
                            } catch (e) {
                              console.error('Error applying times to all dates:', e)
                            }
                          }
                        }}
                        className={cn(
                          "w-full transition-all border-0",
                          applyFeedback 
                            ? "bg-green-500 text-white hover:bg-green-600" 
                            : ""
                        )}
                        size="sm"
                      >
                        {applyFeedback ? "✓ Applied!" : "Apply to All Dates"}
                      </Button>
                    </div>

                    {/* Action Buttons - Side by side */}
                    <div className="flex gap-2">
                      <div className="flex-1 border border-input rounded-md">
                        <Button
                          variant="outline"
                          onClick={() => setShowingTimeSlots(false)}
                          className="w-full border-0"
                          size="sm"
                        >
                          Back
                        </Button>
                      </div>

                      <Button
                        onClick={() => {
                          setShowingTimeSlots(false)
                          setShowCalendar(false)
                        }}
                        disabled={selectedDates.length === 0 || Object.keys(availableSlots).length === 0}
                        className="flex-1"
                        size="sm"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-sm font-semibold mb-4">
                    {mode === 'offer' ? 'Select Available Dates' : 'Select Booking Date'}
                  </h3>
              
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-3">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}
                  className="p-1 rounded-full hover:bg-muted"
                  type="button"
                >
                  <ChevronLeft className="w-4 h-4 text-foreground" />
                </motion.button>
                
                <h2 className="text-sm font-medium text-foreground">
                  {format(currentMonth, 'MMMM yyyy')}
                </h2>
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
                  className="p-1 rounded-full hover:bg-muted"
                  type="button"
                >
                  <ChevronRight className="w-4 h-4 text-foreground" />
                </motion.button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {/* Day names */}
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                    {day}
                  </div>
                ))}
                
                {/* Padding days */}
                {(() => {
                  const monthStart = startOfMonth(currentMonth)
                  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
                  return Array.from({ length: startPadding }, (_, i) => (
                    <div key={`pad-${i}`} className="aspect-square" />
                  ))
                })()}
                
                {/* Actual days */}
                {eachDayOfInterval({ 
                  start: startOfMonth(currentMonth), 
                  end: endOfMonth(currentMonth) 
                }).map(date => {
                  const isSelected = mode === 'offer' 
                    ? selectedDates.some(d => isSameDay(d, date))
                    : isSameDay(date, bookingDate)
                  const isPast = isBefore(date, startOfToday())
                  const isDisabled = isPast
                  
                  return (
                    <motion.button
                      key={date.toISOString()}
                      whileHover={{ scale: isDisabled ? 1 : 0.95 }}
                      whileTap={{ scale: isDisabled ? 1 : 0.9 }}
                      onClick={() => {
                        if (!isDisabled) {
                          if (mode === 'offer') {
                            handleDateSelect(date)
                          } else {
                            setBookingDate(date)
                            setShowCalendar(false)
                          }
                        }
                      }}
                      disabled={isDisabled}
                      type="button"
                      className={cn(
                        "aspect-square rounded-md flex items-center justify-center text-xs transition-colors relative",
                        isSelected && "bg-primary text-primary-foreground",
                        !isSelected && !isDisabled && "hover:bg-muted",
                        isToday(date) && !isSelected && !isDisabled && "border border-primary",
                        !isSameMonth(date, currentMonth) && "text-muted-foreground opacity-50",
                        isDisabled && "opacity-30 cursor-not-allowed",
                        !isSelected && "text-foreground"
                      )}
                    >
                      {format(date, 'd')}
                    </motion.button>
                  )
                })}
              </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {mode === 'offer' ? (
                      <>
                        <div className="flex-1 border border-input rounded-md">
                          <Button
                            variant="outline"
                            onClick={() => setShowCalendar(false)}
                            className="w-full border-0"
                            type="button"
                          >
                            Cancel
                          </Button>
                        </div>
                        <Button
                          onClick={() => {
                            openTimePicker()
                          }}
                          disabled={selectedDates.length === 0}
                          className="flex-1"
                          type="button"
                        >
                          Next
                        </Button>
                      </>
                    ) : (
                      <div className="flex-1 border border-input rounded-md">
                        <Button
                          variant="outline"
                          onClick={() => setShowCalendar(false)}
                          className="w-full border-0"
                          type="button"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

    </Dialog>
  )
}


